import { Select2Data } from '@agent/core/channel-digit/mail/eptica/eptica.helper';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { RxUtil } from '@commons/util/utilities/rx';
import { ILogService, IPromise, IQService, isArray, isDefined } from 'angular';
import { map, Observable, tap } from 'rxjs';

import {
  ChanEnum,
  ChannelUtil,
  ElasticSearchService,
  HttpService,
  MediaType,
  unique,
  User,
  UserElsQuery,
  UserElsQueryParams,
} from '@contact/upgrade';

import { IAgentConfigDeprecatedService } from '../agent/agent-config-deprecated.service';
import { IDirectoryService } from './directory.service';
import { DirectoryContact, IDirectoryContact } from './models/directory-contact';

export const DEFAULT_MAX_SEARCH_RESULTS = 1000;
export const DEFAULT_INIT_MAX_SEARCH_RESULTS_FOR_CSR = 10;

const SEARCH_URL = 'protected/search/';
const PROTECTED_SUPERVISOR = 'protected/supervisor';
const CONTENT_TYPE = 'text/plain; charset=UTF-8';
const NO_RESPONSE_ERROR_MESSAGE = 'The search WS has no response while searching contacts !';
const SEARCHING_ERROR_MESSAGE = 'Error while searching the contacts:';
const RETRIEVING_ERROR_MESSAGE = 'Error while retrieving the contacts:';

export interface ISearchService {
  getSupervisedCsrListLight();

  fetchSupervisorTeams();

  searchCsr(
    teamUUid: string,
    groupUuid: string,
    skillUids: string[],
    keyword?: string,
    channel?: string,
    checkSkills?: boolean,
    domainUuids?: string[]
  ): IPromise<any[]>;

  searchContactByName(autoCompleTextPart: string, repositoryName: string): IPromise<any[]>;

  searchContactByEmail(email: string): IPromise<any[]>;

  searchContactByUUid(uuid: string): IPromise<any>;

  searchContactById(contactId: string, repositoryName: string): IPromise<any[]>;

  searchCorrespondant(text?: string): IPromise<IDirectoryContact[]>;

  searchSkillByUuid(skillUuids: string[]): IPromise<any>;

  searchTeamByUuid(teamUuid: string): IPromise<any>;

  getDomainUser(domainUuid: string): IPromise<User[]>;

  searchCsrObs(
    teamUUid: string,
    groupUuid: string,
    skillUids: string[],
    keyword?: string,
    channel?: string,
    checkSkills?: boolean
  ): Observable<any[]>;

  /**
   * used to replace accented characters by their non accented version
   * @parameter inputValue the string to replace the characters in
   * @output return the inputValue string with accented characters replaced by their non accented version
   */
  normalize(inputValue: string): string;
}

export class SearchService implements ISearchService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    'httpDeprecated',
    'sessionDeprecatedService',
    'directory',
    'elasticSearchService',
    'agentConfigDeprecatedService',
    'http',
  ];
  static instance: SearchService;

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly directoryService: IDirectoryService,
    private readonly elasticSearchService: ElasticSearchService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly http: HttpService
  ) {
    SearchService.instance = this;
  }

  getSupervisedCsrListLight(): Observable<any[]> {
    return this.http
      .withHeaders({
        'Content-Type': 'plain/text; charset=UTF-8',
      })
      .get(`${PROTECTED_SUPERVISOR}/getSupervisedCsrListLight`, {})
      .pipe(map((usrs: any[]) => usrs));
  }

  fetchSupervisorTeams(): Observable<any[]> {
    return this.http
      .withHeaders({
        'Content-Type': 'plain/text; charset=UTF-8',
      })
      .get(`${PROTECTED_SUPERVISOR}/getSupervisedTeamList`, {})
      .pipe(map((teams: any[]) => teams));
  }

  searchCsr(
    teamId: string,
    groupId: string,
    skillIds: string[],
    autocomplete = '',
    channel: string,
    checkSkills: boolean,
    domainUuids: string[] = []
  ): IPromise<any[]> {
    let payload: UserElsQueryParams = {
      teamId,
      groupId,
      skillIds,
      autocomplete,
      channel: {
        value: ChannelUtil.from(channel) ?? ChanEnum.NotSet,
        checkSkills,
      },
      sort: { 'lastName.keyword': 'asc' },
      domainUuids,
    };

    if (!autocomplete) {
      payload.size = DEFAULT_INIT_MAX_SEARCH_RESULTS_FOR_CSR;
    }

    const query = UserElsQuery.buildSearch(payload);
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': CONTENT_TYPE,
        },
      })
      .withData(query.params)
      .post(`${SEARCH_URL}search`, ['user'])
      .then(response => {
        try {
          return response.data.hits.hits.map(rawResult => rawResult._source);
        } catch (e) {
          this.$log.error(`Error while building the CSR candidates for a transfer: ${e}`);
          return [];
        }
      })
      .catch(e => {
        this.$log.error(`Error while retrieving the CSR candidates for a transfer: ${e}`);
        return [];
      });
  }

  searchSkillByUuid(skillUuids: string[]): IPromise<any> {
    const query = {
      types: ['skill'],
      query: {
        bool: {
          must: { terms: { uuid: skillUuids } },
        },
      },
    };
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(query)
      .post(`${SEARCH_URL}search`, ['skill']);
  }

  searchTeamByUuid(teamUuid: string): IPromise<any> {
    const query = {
      types: ['team'],
      query: {
        bool: {
          must: { term: { uuid: teamUuid } },
        },
      },
    };
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(query)
      .post(`${SEARCH_URL}search`, ['team']);
  }

  acMailUsersAndContacts(input: string): IPromise<Select2Data[]> {
    return RxUtil.toPromise(
      this.httpDeprecated
        .asObservable()
        .withConfig({
          headers: {
            'Content-Type': CONTENT_TYPE,
          },
        })
        .get(`${SEARCH_URL}acMailUsersAndContacts`, [input])
        .pipe(
          map((usrs: any[]) => {
            if (usrs && usrs['data']) {
              usrs['data'] = unique(usrs['data'], 'id');
            }
            return usrs as Select2Data[];
          })
        ),
      this.$q
    );
  }

  searchCsrObs(
    teamId: string,
    groupId: string,
    skillIds: string[],
    autocomplete = '',
    channel: string,
    checkSkills: boolean
  ): Observable<any[]> {
    const query = UserElsQuery.buildSearch({
      teamId,
      groupId,
      skillIds,
      autocomplete,
      channel: {
        value: ChannelUtil.from(channel) ?? ChanEnum.NotSet,
        checkSkills,
      },
      sort: { 'lastName.keyword': 'asc' },
    });
    return this.elasticSearchService
      .searchOnSeveralTypesDeprecated({
        ...query,
        ['types']: ['user'],
      })
      .pipe(
        map(data => data),
        tap(
          next => this.$log.debug(`[search-service] searchCsrObs found : [${JSON.stringify(next)}]`),
          error => this.$log.error(`[search-service] searchCsrObs failed : [${JSON.stringify(error)}]`),
          () => this.$log.debug(`[search-service] searchCsrObs done`)
        )
      );
  }

  searchContactByName(autoCompleTextPart: string, repositoryName: string): IPromise<any[]> {
    if (autoCompleTextPart != null && autoCompleTextPart.length < 2) {
      return this.$q.resolve([]);
    }
    const query = this.buildSearchContactQuery(autoCompleTextPart, repositoryName);
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': CONTENT_TYPE,
        },
      })
      .withData(query)
      .post(`${SEARCH_URL}search`, ['contact'])
      .then(response => {
        try {
          if (response) {
            return response.data.hits.hits.map(rawResult => rawResult._source);
          } else {
            this.$log.error(NO_RESPONSE_ERROR_MESSAGE);
            return [];
          }
        } catch (e) {
          this.$log.error(`${SEARCHING_ERROR_MESSAGE} ${e}`);
          return [];
        }
      })
      .catch(e => {
        this.$log.error(`${RETRIEVING_ERROR_MESSAGE} ${e}`);
        return [];
      });
  }

  searchContactByEmail(email: string): IPromise<any[]> {
    const query = this.buildSearchContactByEmailQuery(email);
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': CONTENT_TYPE,
        },
      })
      .withData(query)
      .post(SEARCH_URL + 'search', ['contact'])
      .then(response => {
        try {
          if (response && response.data.hits) {
            return response.data.hits.hits.map(rawResult => rawResult._source);
          } else {
            this.$log.error(NO_RESPONSE_ERROR_MESSAGE);
            return [];
          }
        } catch (e) {
          this.$log.error(`${SEARCHING_ERROR_MESSAGE} ${e}`);
          return [];
        }
      })
      .catch(e => {
        this.$log.error(`${RETRIEVING_ERROR_MESSAGE} ${e}`);
        return [];
      });
  }

  searchContactByUUid(uuid: string): IPromise<any> {
    const query = this.buildSearchContactByUuidQuery(uuid);
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': CONTENT_TYPE,
        },
      })
      .withData(query)
      .post(SEARCH_URL + 'search', ['contact'])
      .then(response => {
        try {
          if (response && response.data.hits) {
            let allResults = response.data.hits.hits.map(rawResult => rawResult._source);
            if (allResults.length > 0) {
              if (allResults.length > 1) {
                this.$log.warn(`The search of a contact by uuid returned multiple results for uuid=${uuid} !`);
              }
              return allResults[0];
            } else {
              this.$log.warn(`The search of a contact by uuid have no results for uuid=${uuid} !`);
              return undefined;
            }
          } else {
            this.$log.error(NO_RESPONSE_ERROR_MESSAGE);
            return undefined;
          }
        } catch (e) {
          this.$log.error(`${SEARCHING_ERROR_MESSAGE} ${e}`);
          return undefined;
        }
      })
      .catch(e => {
        this.$log.error(`${RETRIEVING_ERROR_MESSAGE} ${e}`);
        return undefined;
      });
  }

  searchContactById(contactId: string, repositoryName: string): IPromise<any[]> {
    const query = this.buildSearchContactByIdQuery(contactId, repositoryName);
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': CONTENT_TYPE,
        },
      })
      .withData(query)
      .post(SEARCH_URL + 'search', ['contact'])
      .then(response => {
        try {
          if (response && response.data.hits) {
            let allResults = response.data.hits.hits.map(rawResult => rawResult._source);
            if (allResults.length > 0) {
              if (allResults.length > 1) {
                this.$log.warn(`The search of a contact by id returned multiple results for uuid=${contactId} !`);
              }
              return allResults;
            } else {
              this.$log.warn(`The search of a contact by id have no results for uuid=${contactId} !`);
            }
          } else {
            this.$log.error(NO_RESPONSE_ERROR_MESSAGE);
            return [];
          }
        } catch (e) {
          this.$log.error(`${SEARCHING_ERROR_MESSAGE} ${e}`);
          return [];
        }
      })
      .catch(e => {
        this.$log.error(`${RETRIEVING_ERROR_MESSAGE} ${e}`);
        return [];
      });
  }

  searchCorrespondant(text?: string): IPromise<IDirectoryContact[]> {
    return this.directoryService
      .getExternalCorrespondents(
        this.sessionDeprecatedService.getProfile().domainId,
        [],
        this.agentConfigDeprecatedService.agentTeam,
        [],
        text,
        [MediaType.MAIL]
      )
      .then(success => success.data.map((external: any) => DirectoryContact.fromDto(external)));
  }

  normalize(inputValue: string): string {
    // transform 'àé...' to 'ae...'
    return inputValue
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private buildSearchContactQuery(autoCompleTextPart: string, repositoryName: string): IPromise<any> {
    const query = this._searchContactQuerySkeletton();

    query.query = {
      bool: {
        must: [],
      },
    };

    query.query.bool.must.push({
      bool: {
        should: [
          {
            wildcard: { 'contactLastName.keyword': '*' + (autoCompleTextPart || '').toLowerCase() + '*' },
          },
          {
            wildcard: { 'contactFirstName.keyword': '*' + (autoCompleTextPart || '').toLowerCase() + '*' },
          },
          {
            wildcard: { 'addresses.uri': '*' + (autoCompleTextPart || '').toLowerCase() + '*' },
          },
        ],
        minimum_should_match: 1,
      },
    });

    query.query.bool.must.push({
      term: { 'addresses.channel': 'MAIL_INCOMING' },
    });
    query.query.bool.must.push({ term: { 'customerRepositoryName.keyword': repositoryName } });
    return query;
  }

  private buildSearchContactByEmailQuery(email: string): IPromise<any> {
    const query = this._searchContactQuerySkeletton();

    query.query = {
      bool: {
        must: [],
      },
    };

    query.query.bool.must.push({
      term: { 'addresses.uri': (email || '').toLowerCase() },
    });

    query.query.bool.must.push({
      term: { 'addresses.channel': 'MAIL_INCOMING' },
    });

    return query;
  }

  private buildSearchContactByUuidQuery(uuid: string): IPromise<any> {
    const query = this._searchContactQuerySkeletton();

    query.query = {
      bool: {
        must: [],
      },
    };

    query.query.bool.must.push({
      term: { uuid: uuid },
    });

    return query;
  }

  private buildSearchContactByIdQuery(contactId: string, repositoryName: string): IPromise<any> {
    const query = this._searchContactQuerySkeletton();

    query.query = {
      bool: {
        must: [],
      },
    };

    query.query.bool.must.push({
      bool: {
        should: {
          wildcard: { contactId: `*${contactId.toLowerCase()}*` },
        },
      },
    });
    query.query.bool.must.push({ term: { 'customerRepositoryName.keyword': repositoryName } });
    return query;
  }

  private _searchContactQuerySkeletton(): any {
    return {
      from: 0,
      size: DEFAULT_MAX_SEARCH_RESULTS,
      query: {
        bool: {
          must: [
            {
              match_all: {},
            },
          ],
        },
      },
    };
  }

  private searchDomainListUserRequest(from: number, maxEntries: number, domainUuid: string): any {
    return {
      types: ['user'],
      sort: [{ 'firstName.keyword': { order: 'asc' } }],
      from: from,
      size: maxEntries,
      query: {
        bool: {
          must: {
            match_all: {},
          },
          filter: {
            bool: {
              must: {
                terms: {
                  domainUuid: [domainUuid],
                },
              },
            },
          },
        },
      },
    };
  }

  getDomainUser(domainUuid: string): IPromise<User[]> {
    const deferred = this.$q.defer<User[]>();
    const result: User[] = [];
    RxUtil.toPromise(
      this.elasticSearchService.searchOnSeveralTypesDeprecated(this.searchDomainListUserRequest(0, 100, domainUuid)),
      this.$q
    ).then(r => {
      if (isDefined(r.hits) && isArray(r.hits.hits)) {
        let m = 0;
        const limitm = r.hits.hits.length;
        for (m; m < limitm; m++) {
          result.push({
            firstname: r.hits.hits[m]._source.firstName.toString(),
            lastname: r.hits.hits[m]._source.lastName.toString().toUpperCase(),
            id: r.hits.hits[m]._id,
            name: '',
          });
        }
      }
      deferred.resolve(result);
    });
    return deferred.promise;
  }
}

export default 'search';
